import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "@config/axios/axios.config";
import { QUERY_KEYS, SERVER_ROUTES } from "@constants/api.routes";

/** Sector row as returned by the institutes sector API (shape may vary). */
export type SectorListItem = Record<string, unknown> & {
	uuid?: string;
	id?: string;
	label?: string;
	name?: string;
	value?: string;
};

function parseSectorsResponse(response: unknown): SectorListItem[] {
	if (Array.isArray(response)) {
		return response as SectorListItem[];
	}
	const r = response as { data?: unknown } | null | undefined;
	const d = r?.data;
	if (Array.isArray(d)) {
		return d as SectorListItem[];
	}
	if (d && typeof d === "object") {
		const obj = d as Record<string, unknown>;
		if (Array.isArray(obj.data)) return obj.data as SectorListItem[];
		if (Array.isArray(obj.items)) return obj.items as SectorListItem[];
		if (Array.isArray(obj.rows)) return obj.rows as SectorListItem[];
	}
	return [];
}

/**
 * Sectors for dropdowns. Pass `searchTerm` to filter via API query param `search` (same pattern as institute portal lookups).
 */
export function useGetSectors(searchTerm?: string) {
	const normalizedSearchTerm = searchTerm?.trim() || undefined;

	const { data, isLoading, isFetching, error } = useQuery<SectorListItem[]>({
		queryKey: [QUERY_KEYS.INSTITUTES.SECTORS, normalizedSearchTerm ?? ""],
		queryFn: async () => {
			const params = normalizedSearchTerm ? { search: normalizedSearchTerm } : {};
			const response = await api.get(SERVER_ROUTES.INSTITUTES.SECTORS, { params });
			return parseSectorsResponse(response);
		},
		placeholderData: keepPreviousData,
	});

	return {
		sectors: Array.isArray(data) ? data : [],
		/** True only when there is no data yet (first load). */
		loadingSectors: isLoading,
		isFetchingSectors: isFetching,
		error,
	};
}
